import * as THREE from "three";
import { World } from "./World";
import { Portal } from "./Portal";
import { gsap, Power4 } from "gsap";
import {
    vDefaultShader, 
    fFireShader,
    fHaloShader,
    fLibraryShader,
    fLibraryWallShader
} from '../shaders/';
import { BookManager } from "../components/BookManager";
import { RoachManager } from "../components/RoachManager";
import { EmitterManager } from '../components/EmitterManager';
import { ParticlesTrail } from "../components/ParticlesTrail";
import { AppEvent, LibraryView, worlds } from "../constants";
import * as CameraUtils from 'three/examples/jsm/utils/CameraUtils';
import { FlyingPapers } from "../components/FlyingPapers";


export class LibraryWorld extends World {

    constructor({ name, model, assets }) {
        super({ name, model, assets });

        this.headTransitionSpeed = 2; // overrides the one in World.js

        this.camera.near = 1;
        this.camera.far = 1000;
        this.camera.position.set(-100, 20, 20);
        this.camera.fov = 60;

        this.corners = {
            bottomLeft: new THREE.Vector3(),
            bottomRight: new THREE.Vector3(),
            topLeft: new THREE.Vector3(),
        }

        // Portals
        this.currentPortal = null;
        this.portalHover = null;
        this.portalClickInitiated = null;

        // Three setup
        this.gamma = 2.8
        this.ratioR = 1
        this.ratioG = 0
        this.ratioB = 0
        this.ratioEffect=0

        // These are referenced by WorldHolder (as per other worlds)
        this.titleBrightness = 0.4;
        this.titleDisplace = 0;

        // Where we are
        this.view = LibraryView.GLOBAL;
        this.currentStep = -1;

        this.frontCameraStep = 0;

        this.createTrail();
    }

    createTrail() {
        this.trail = new ParticlesTrail({
            scene: this.scene,
            count: 150,
            duration: 2,
        })
    }

    updateCorners(portal) {
        // the actual 3D object of the Portal class is exposed via a getter
        const { portal3d } = portal;
        const { min, max } = portal3d.geometry.boundingBox;
        const { bottomLeft, bottomRight, topLeft } = this.corners;
        portal3d.localToWorld(bottomLeft.set(min.x, min.y, 0));
        portal3d.localToWorld(bottomRight.set(max.x, min.y, 0));
        portal3d.localToWorld(topLeft.set(min.x, max.y, 0));
    }

    processModel() {
        super.processModel();

        // Create Portal classes
        this.portals = worlds.map((config, index) => new Portal({
            id: index + 1, // original code ran from 1 to 7 to match IDs in models
            config,
            assets: this.assets,
            libraryModel: this.model,
        }));

        // SHELVES
        this.shelves = this.model.scene.getObjectByName("Shelves");

        const defaultUniforms = {
            mixDiffuse: { value: 0 },
            hatchingMap: { value: this.assets.textures.hatchC },
            whiteLevel: { value: 1 },
            blackLevel: { value: 0 },
            time: { value: 0 },
        }

        this.matBase = new THREE.ShaderMaterial({
            uniforms: {
                ...defaultUniforms,
                map: { value: this.shelves.material.map },
                heightMap: { value: this.assets.textures.shelvesHeight },
                noiseMap: { value: this.assets.textures.noise },
                ratioEffect: { value: 0 },
                useEffect: {value: 0},
                lightIntensity: {value: 1},
                hatchingScale: { value: 40 },
                gamma: { value: 2.8 },
                ratioR: { value: 1 },
                ratioG: { value: 0 },
                ratioB: { value: 0 },
            },
            vertexShader: vDefaultShader,
            fragmentShader: fLibraryShader,
            transparent: true,
        })
        this.matShelves = this.matBase.clone();
        this.matShelves.uniforms.useEffect.value = 1.
        this.shelves.material = this.matShelves;

        // accessories

        this.fireplace = this.model.scene.getObjectByName("Fireplace");
        const oldMatAccessories = this.fireplace.material
        this.mapAccessories = oldMatAccessories.map;

        this.matAccessories = this.matBase.clone();
        this.matAccessories.uniforms.map.value = this.mapAccessories;
        this.matAccessories.uniforms.hatchingScale.value = 30;

        // Halos
        const haloMap = this.model.scene.getObjectByName("halo0").material.map
        this.matHalos = new THREE.ShaderMaterial({
            uniforms: {
                ...defaultUniforms,
                map: { value: haloMap },
                noiseMap: { value: this.assets.textures.noise },
                hatchingScale: { value: 2 },
                gamma: { value: 3.5 },
                lightIntensity: {value: 1},
            },
            vertexShader: vDefaultShader,
            fragmentShader: fHaloShader,
            transparent: true,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
        })

        this.halos = []

        for (let i = 0; i < 8; i++) {
            const halo = this.model.scene.getObjectByName("halo" + i);
            this.halos.push(halo)
            halo.material = this.matHalos;
        }

        // Fire
        this.matFire = new THREE.ShaderMaterial({
            uniforms: {
                ...defaultUniforms,
                noiseMap: { value: this.assets.textures.noise },
                hatchingScale: { value: 2 },
                gamma: { value: 3.5 },
            },
            vertexShader: vDefaultShader,
            fragmentShader: fFireShader,
            transparent: true,
            blending: THREE.AdditiveBlending,
            depthWrite: false,

        })

        this.fires = []
        for (let i = 0; i < 2; i++) {
            const fire = this.model.scene.getObjectByName("fire" + i);
            this.fires.push(fire)
            fire.material = this.matFire;
        }

        // Wall
        this.wall = this.model.scene.getObjectByName("wall");
        this.wall.material = new THREE.ShaderMaterial({
            uniforms: {
                ...defaultUniforms,
                map: { value: this.wall.material.map },
                noiseMap: { value: this.assets.textures.noise },
                hatchingScale: { value: 20 },
                mixDiffuse: { value: 0.5 },
                lightIntensity: {value: 1},
                gamma: { value: 4 },
                shadowRatio: { value: 1 },
            },
            vertexShader: vDefaultShader,
            fragmentShader: fLibraryWallShader,
        })


        // Roaches
        this.roachManager = new RoachManager({
            model: this.model,
            scene: this.scene,
            count: 10,
        });

        // Emitters
        this.emitterManager = new EmitterManager({
            model: this.model,
            assets: this.assets,
            scene: this.scene,
        });

        // Books
        this.books = new BookManager({
            model: this.model,
            assets: this.assets
        });

        this.container.traverse(obj => {
            if (obj.material === oldMatAccessories) {
                obj.material = this.matAccessories
            }
        });

        // Flying papers

        this.paper = this.model.scene.getObjectByName("paper");
        this.paper.material = new THREE.MeshLambertMaterial({
            map: this.paper.material.map,
            side: THREE.DoubleSide
        })

        this.flyingPapers = new FlyingPapers({scene: this.scene, particleBase : this.paper});
    }

    createInitialScreenshots({ renderer }) {
        this.portals.forEach((portal, index) => {

            // Align the cameras
            const { world } = portal;
            this.camera.position.copy(this.cameras[index].position);
            this.camera.lookAt(this.targets[index].position);
            world.camera.position.copy(this.camera.position);
            world.camera.quaternion.copy(this.camera.quaternion);

            // Take the shot
            this.screenshotPortal({ portal, renderer });

            // Render now
            renderer.render(this.scene, this.camera);
        });

        // Reset camera
        this.camera.position.copy(this.cameras[7].position);
        this.currentStep = 7;
        this.updateStep();

        this.currentPortal = this.portals[0];

        // Reset the step
        this.currentStep = 0;
    }

    screenshotPortal({ portal, renderer }) {
        this.updateCorners(portal);
        const { bottomLeft, bottomRight, topLeft } = this.corners;
        CameraUtils.frameCorners(portal.world.camera, bottomLeft, bottomRight, topLeft, false);

        // Portal handles actual screenshot of itself
        portal.takeScreenshot({ renderer });
    }

    updateStep(step) {
        if (step === this.currentStep) {
            return;
        }

        const portal = this.portals.find(portal => portal.id === step);

        let gamma;

        if (portal) {
            this.currentPortal = portal;
            gamma = 2.2;
            this.emit(AppEvent.LIBRARY_VIEW_CHANGE, LibraryView.IN_PORTAL);
        } else {
            gamma = 3.1;

            if (this.currentPortal) {
                this.emit(AppEvent.LIBRARY_VIEW_CHANGE, LibraryView.GLOBAL);
                // Do we want to kill currentPortal here? ie. this.currentPortal = null;
            }
        }

        gsap.to(this, { gamma, duration: 2.5, ease: Power4.easeInOut });
        super.updateStep(step);
    }

    onUpdateStepStart(step, exitFromWorld) {
        super.onUpdateStepStart(step, exitFromWorld);

        if (!exitFromWorld) {
            this.books.switchBook(0);
        }

        if (step === 0) {
            this.view = LibraryView.GLOBAL;
        } else {
            if (this.controls) {
                this.controls.enabled = false;
            }
        }
    }

    onUpdateStepComplete(step) {
        super.onUpdateStepComplete(step);

        if (step !== 0) {
            this.view = LibraryView.FOCUS;
            this.emit(AppEvent.LIBRARY_VIEW_CHANGE, this.view);
            this.controls.enabled = false
        } else {
            if (this.controls) {
                this.controls.enabled = true;
            }
        }

        this.books.onUpdateStepComplete(step);
    }

    loop({ time, deltaTime }) {
        super.loop({ time, deltaTime });

        // Update the books
        this.books.update({ time, deltaTime });

        // halos
        this.halos.forEach((halo, i) => {
            halo.lookAt(this.camera.position)
            halo.scale.y = 1 + Math.abs(Math.sin(i * 1.5 + time * 0.5));
            halo.scale.x = halo.scale.z = halo.scale.y / 2;
        });

        this.matHalos.uniforms.time.value = time;

        // fires
        this.fires.forEach(fire => fire.lookAt(this.camera.position));
        this.matFire.uniforms.time.value = time;

        // Update all emitters
        this.emitterManager.update(deltaTime);

        // Update trail
        if (this.trail) this.trail.updateParticles(deltaTime);

        // Update all roaches
        this.roachManager.update(time);

        // wall
        if (this.wall) {
            this.wall.material.uniforms.time.value = time
        }

        this.ratioR = 0.2 + Math.abs(Math.sin(time * 0.456 + 3.222 + Math.sin(time * 4.331 + 1.234) * 0.1)) * 0.8;
        this.ratioG = 0.2 + Math.abs(Math.sin(time * 0.138 + 2.456 + Math.cos(time * 1.447 + 2.564) * 0.2)) * 0.8;
        this.ratioB = 0.2 + Math.abs(Math.sin(time * 0.265 + 1.325 + Math.sin(time * 2.524 + 3.663) * 0.3)) * 0.8;

        // Final Effect

        if (this.flyingPapers.running){
            this.flyingPapers.updateParticles(deltaTime);
        }
        
        if (this.view === LibraryView.FOCUS) {

            this.floatingHeadEnabled = false;

            const camPos = this.cameras[this.currentStep].position
            const targetPos = this.targets[this.currentStep].position

            let direction = new THREE.Vector3().subVectors(camPos, targetPos)
            const axisZ = new THREE.Vector3(0, 0, 1)

            const rot = new THREE.Quaternion().setFromUnitVectors(axisZ, direction.clone().normalize())
            const axisRotY = new THREE.Vector3(0, 1, 0).applyQuaternion(rot);
            const axisRotX = new THREE.Vector3(1, 0, 0).applyQuaternion(rot);
            direction = direction.applyAxisAngle(axisRotX, -this.mouse.y * .05)
            direction = direction.applyAxisAngle(axisRotY, this.mouse.x * .2)
            const targetCamPos = new THREE.Vector3().addVectors(targetPos, direction)

            this.camera.position.lerp(targetCamPos, deltaTime * 2);
            this.camera.lookAt(targetPos)

        } else {
            this.floatingHeadEnabled = true
        }
    }

    mouseDown() {
        this.raycast(AppEvent.CLICK_INITIATED);
    }

    mouseUp() {
        this.raycast(AppEvent.CLICK_ENDED);
    }

    raycast(event) {
        if (this.transitionToStepProgress !== 1) {
            return;
        }

        super.raycast();

        const intersects = this.raycaster.intersectObjects(this.portals.map(p => p.collidable));

        if (intersects.length > 0) {
            // console.log("raycast", intersects[0].object.name);
            const targetStep = Number(intersects[0].object.name.slice(-1));

            if (this.portalHover !== targetStep) {
                this.portalHover = targetStep;
            }

            this.handleRaycastEvent(event, targetStep);
        } else {
            this.portalHover = null

            if ([AppEvent.CLICK_INITIATED, AppEvent.CLICK_ENDED].find(e => e === event)) {
                this.portalClickInitiated = null
            }
        }
    }

    handleRaycastEvent(event, targetStep) {
        switch (event) {
            case AppEvent.CLICK_INITIATED:
                this.portalClickInitiated = targetStep;

                // Tell the WorldHolder so it can play audio
                this.emit(AppEvent.CLICK_INITIATED, targetStep);
                break;
            case AppEvent.CLICK_ENDED:
                if (this.portalClickInitiated === targetStep) {
                    this.portalClickInitiated = null;
                    // console.log("raycast", targetStep, event)

                    if (this.currentStep !== targetStep) {
                        this.focusOnPortal(targetStep)
                    } else {
                        this.emit(AppEvent.PORTAL_CLICK, targetStep);
                    }
                }
                break;
        }
    }

    focusOnPortal(target) {
        this.currentStep = target;
        this.currentPortal = this.portals.find(portal => portal.id === this.currentStep);
        this.updateStep();
    }

    replaceCameraInFrontOfPortal(step, exitFromWorld = false, distance=20) {
        const camPosStep = this.cameras[step].position.clone()
        const targetPosStep = this.targets[step].position.clone()
        const direction = new THREE.Vector3().subVectors(camPosStep, targetPosStep).normalize()
        direction.multiplyScalar(distance)
        camPosStep.add(direction)
        this.cameras[0].position.copy(camPosStep)
        this.currentStep = 0
        this.updateStep({ exitFromWorld })
    }

    launchPapers(params = {}){
        const delay = params.delay || 0;

        this.flyingPapers.launch({delay});

        const tl = gsap.timeline();
        tl.addLabel("start")
        tl.set(this, {ratioEffect:1, delay}, "start+=2" )

        tl.to(this, {
            ratioEffect : 0,
            duration : 20,
            ease: Power4.easeOut
        })

        tl.to(this.wall.material.uniforms.shadowRatio, {
            value: 0,
            duration : 3,
            ease: Power4.easeOut
        }, "start+=5")

    }

    set gamma(v) {
        this._gamma = v;
        this.matShelves.uniforms.gamma.value = this._gamma
        this.matAccessories.uniforms.gamma.value = this._gamma
        this.matHalos.opacity = this._gamma / 8
        this.matFire.opacity = this._gamma / 6
    }

    set ratioR(v) {
        this._ratioR = v
        this.matShelves.uniforms.ratioR.value = v
        this.matAccessories.uniforms.ratioR.value = v
    }

    set ratioG(v) {
        this._ratioG = v
        this.matShelves.uniforms.ratioG.value = v
        this.matAccessories.uniforms.ratioG.value = v
    }

    set ratioB(v) {
        this._ratioB = v
        this.matShelves.uniforms.ratioB.value = v
        this.matAccessories.uniforms.ratioB.value = v
    }

    set ratioEffect(v) {
        this._ratioEffect = v
        this.matShelves.uniforms.ratioEffect.value = v
        this.matShelves.uniforms.lightIntensity.value = 1 + v
        this.wall.material.uniforms.lightIntensity.value = 1 + v * 1
        this.matHalos.uniforms.lightIntensity.value = 1 + v * 1.5;
        this.matAccessories.uniforms.lightIntensity.value = 1 + v * 1;
    }

    get gamma() { return this._gamma }
    get ratioR() { return this._ratioR }
    get ratioG() { return this._ratioG }
    get ratioB() { return this._ratioB }
    get ratioEffect() { return this._ratioEffect }

    get currentCamera() {
        return this.currentStep === 0 ? this.camera : this.cameras[this.currentStep];
    }
}